import { createClientLogger, logClientError } from '@/client/logging/client-logger';
import { ReviewEntity } from '@/entities/review.entity';
import { TableCheckboxData } from '@/ui/Table';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { showSelectionLimitReachedToast } from '@/ui/selection-limit-toast';
import { useMarketplaceApi } from '@/context/marketplace.context';

type UseFeedbacksTableSelectionArgs = {
    accessToken: string;
    selectionSessionId: string;
    pickedReviews: string[];
    setPickedReviews?: (value: string[]) => void;
    reviews: ReviewEntity[];
    maxSelectedItems: number | null;
    enqueueReviewSelectionMutation: <T>(mutation: () => Promise<T>) => Promise<T>;
    waitForReviewSelectionReconcile: () => Promise<void>;
};

const feedbacksTableSelectionLogger = createClientLogger({
    scope: 'feedbacks-table-selection',
});

export const useFeedbacksTableSelection = ({
    accessToken,
    selectionSessionId,
    pickedReviews,
    setPickedReviews,
    reviews,
    maxSelectedItems,
    enqueueReviewSelectionMutation,
    waitForReviewSelectionReconcile,
}: UseFeedbacksTableSelectionArgs) => {
    const { reviewsApi } = useMarketplaceApi();

    const pickedReviewIds = useMemo(() => new Set(pickedReviews), [pickedReviews]);
    const pickedReviewsRef = useRef<string[]>(pickedReviews);

    useEffect(() => {
        pickedReviewsRef.current = pickedReviews;
    }, [pickedReviews]);

    const onHeaderCheckboxChange = useCallback(
        async (checked: boolean) => {
            if (!setPickedReviews || !selectionSessionId) {
                return;
            }

            await waitForReviewSelectionReconcile();
            const currentPickedReviews = pickedReviewsRef.current;

            if (checked) {
                const pickedReviewById = new Set(currentPickedReviews);
                const addCandidateIds: string[] = [];

                for (const item of reviews) {
                    if (!pickedReviewById.has(item.id)) {
                        addCandidateIds.push(item.id);
                    }
                    pickedReviewById.add(item.id);
                }

                const availableSlots =
                    maxSelectedItems === null
                        ? addCandidateIds.length
                        : Math.max(0, maxSelectedItems - currentPickedReviews.length);

                if (availableSlots === 0) {
                    if (maxSelectedItems !== null) {
                        showSelectionLimitReachedToast({ maxSelectedItems, scope: 'reviews' });
                    }
                    return;
                }

                const addIds = addCandidateIds.slice(0, availableSlots);

                if (addIds.length) {
                    void enqueueReviewSelectionMutation(() =>
                        reviewsApi.updateReviewSelectionSession(
                            selectionSessionId,
                            { addIds },
                            { authToken: accessToken },
                        ),
                    ).catch((error) =>
                        logClientError({
                            logger: feedbacksTableSelectionLogger,
                            message:
                                '[client.review-selection.page-selection-add] Failed to sync page review selection additions.',
                            error,
                            context: {
                                selectionSessionId,
                                addedIds: addIds.length,
                            },
                        }),
                    );
                }

                if (addIds.length < addCandidateIds.length && maxSelectedItems !== null) {
                    showSelectionLimitReachedToast({ maxSelectedItems, scope: 'reviews' });
                }

                const nextPickedReviewIds = new Set(currentPickedReviews);
                for (const reviewId of addIds) {
                    nextPickedReviewIds.add(reviewId);
                }

                const nextPickedReviews = [...nextPickedReviewIds];
                pickedReviewsRef.current = nextPickedReviews;
                setPickedReviews(nextPickedReviews);
                return;
            }

            const reviewsOnPageIds = new Set(reviews.map((review) => review.id));
            const removeIds = currentPickedReviews.filter((pickedReviewId) => reviewsOnPageIds.has(pickedReviewId));

            if (removeIds.length) {
                void enqueueReviewSelectionMutation(() =>
                    reviewsApi.updateReviewSelectionSession(
                        selectionSessionId,
                        { removeIds },
                        { authToken: accessToken },
                    ),
                ).catch((error) =>
                    logClientError({
                        logger: feedbacksTableSelectionLogger,
                        message:
                            '[client.review-selection.page-selection-remove] Failed to sync page review selection removals.',
                        error,
                        context: {
                            selectionSessionId,
                            removedIds: removeIds.length,
                        },
                    }),
                );
            }

            const nextPickedReviews = currentPickedReviews.filter(
                (pickedReviewId) => !reviewsOnPageIds.has(pickedReviewId),
            );
            pickedReviewsRef.current = nextPickedReviews;
            setPickedReviews(nextPickedReviews);
        },
        [
            accessToken,
            enqueueReviewSelectionMutation,
            maxSelectedItems,
            reviews,
            selectionSessionId,
            setPickedReviews,
            waitForReviewSelectionReconcile,
        ],
    );

    const onCheckboxChange = useCallback(
        async (checked: boolean, item: ReviewEntity) => {
            if (!setPickedReviews || !selectionSessionId) {
                return;
            }

            const currentPickedReviews = pickedReviewsRef.current;
            const currentPickedReviewIds = new Set(currentPickedReviews);

            if (checked) {
                if (currentPickedReviewIds.has(item.id)) {
                    return;
                }

                if (maxSelectedItems !== null && currentPickedReviews.length >= maxSelectedItems) {
                    await waitForReviewSelectionReconcile();

                    const reconciledPickedReviews = pickedReviewsRef.current;
                    const reconciledPickedReviewIds = new Set(reconciledPickedReviews);

                    if (reconciledPickedReviewIds.has(item.id)) {
                        return;
                    }

                    if (reconciledPickedReviews.length >= maxSelectedItems) {
                        showSelectionLimitReachedToast({ maxSelectedItems, scope: 'reviews' });
                        return;
                    }

                    void enqueueReviewSelectionMutation(() =>
                        reviewsApi.updateReviewSelectionSession(
                            selectionSessionId,
                            { addIds: [item.id] },
                            { authToken: accessToken },
                        ),
                    ).catch((error) =>
                        logClientError({
                            logger: feedbacksTableSelectionLogger,
                            message:
                                '[client.review-selection.single-selection-add] Failed to sync single review selection add.',
                            error,
                            context: {
                                selectionSessionId,
                                reviewId: item.id,
                            },
                        }),
                    );

                    const nextPickedReviews = [...reconciledPickedReviews, item.id];
                    pickedReviewsRef.current = nextPickedReviews;
                    setPickedReviews(nextPickedReviews);
                    return;
                }

                void enqueueReviewSelectionMutation(() =>
                    reviewsApi.updateReviewSelectionSession(
                        selectionSessionId,
                        { addIds: [item.id] },
                        { authToken: accessToken },
                    ),
                ).catch((error) =>
                    logClientError({
                        logger: feedbacksTableSelectionLogger,
                        message:
                            '[client.review-selection.single-selection-add] Failed to sync single review selection add.',
                        error,
                        context: {
                            selectionSessionId,
                            reviewId: item.id,
                        },
                    }),
                );

                const nextPickedReviews = [...currentPickedReviews, item.id];
                pickedReviewsRef.current = nextPickedReviews;
                setPickedReviews(nextPickedReviews);
                return;
            }

            void enqueueReviewSelectionMutation(() =>
                reviewsApi.updateReviewSelectionSession(
                    selectionSessionId,
                    { removeIds: [item.id] },
                    { authToken: accessToken },
                ),
            ).catch((error) =>
                logClientError({
                    logger: feedbacksTableSelectionLogger,
                    message:
                        '[client.review-selection.single-selection-remove] Failed to sync single review selection removal.',
                    error,
                    context: {
                        selectionSessionId,
                        reviewId: item.id,
                    },
                }),
            );

            const nextPickedReviews = currentPickedReviews.filter((pickedReviewId) => pickedReviewId !== item.id);
            pickedReviewsRef.current = nextPickedReviews;
            setPickedReviews(nextPickedReviews);
        },
        [
            accessToken,
            enqueueReviewSelectionMutation,
            maxSelectedItems,
            selectionSessionId,
            setPickedReviews,
            waitForReviewSelectionReconcile,
        ],
    );

    const isHeaderChecked = useMemo(() => {
        return !!reviews.length && reviews.every((review) => pickedReviewIds.has(review.id));
    }, [pickedReviewIds, reviews]);

    const checkboxData = useMemo<TableCheckboxData[]>(() => {
        const isSelectionLimitReached = maxSelectedItems !== null && pickedReviews.length >= maxSelectedItems;

        return reviews.map((review) => ({
            checked: pickedReviewIds.has(review.id),
            disabled: isSelectionLimitReached && !pickedReviewIds.has(review.id),
        }));
    }, [maxSelectedItems, pickedReviewIds, pickedReviews.length, reviews]);

    return {
        onHeaderCheckboxChange,
        onCheckboxChange,
        isHeaderChecked,
        checkboxData,
    };
};
